import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Mail, CheckCircle2 } from "lucide-react";

const GRAD = "linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // 🔒 Backend now returns the same generic shape whether the email exists
      // or not (prevents account enumeration + direct API takeover). The reset
      // link is only delivered via the verified email channel.
      await api.post("/auth/forgot-password", { email });
      setSubmitted(true);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12"
      style={{ background: "#f4f6ff", fontFamily: "Inter, system-ui, sans-serif" }}>
      <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full blur-[120px] pointer-events-none"
        style={{ background: "rgba(139,92,246,.12)" }} />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full blur-[100px] pointer-events-none"
        style={{ background: "rgba(59,130,246,.10)" }} />

      <div className="relative z-10 w-full max-w-md">
        <Link to="/login" className="text-sm inline-flex items-center gap-2 mb-6 font-medium hover:opacity-70 transition"
          style={{ color: "#4a4a6a" }} data-testid="forgot-back-login">
          <ArrowLeft className="w-4 h-4" /> Retour à la connexion
        </Link>

        <div className="rounded-3xl p-8 sm:p-10 border" style={{ background: "#fff", borderColor: "#e2e8f8", boxShadow: "0 20px 60px -20px rgba(59,130,246,.2)" }}>
          {/* Logo */}
          <div className="flex items-center gap-2.5 mb-8">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-extrabold"
              style={{ background: GRAD, boxShadow: "0 4px 12px -4px rgba(59,130,246,.6)" }}>N</div>
            <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "1.2rem", color: "#0d0d1a" }}>
              Neobk<span style={{ background: GRAD, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>.</span>Pro
            </span>
          </div>

          {submitted ? (
            // 🔒 Generic confirmation — does NOT reveal whether the email exists.
            <div className="text-center" data-testid="forgot-submitted">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
                style={{ background: "rgba(16,185,129,.1)" }}>
                <CheckCircle2 className="w-7 h-7" style={{ color: "#10b981" }} />
              </div>
              <h1 className="text-2xl font-bold mb-2"
                style={{ fontFamily: "'Space Grotesk', sans-serif", color: "#0d0d1a", letterSpacing: "-0.02em" }}>
                Vérifiez votre email
              </h1>
              <p className="text-sm mb-6" style={{ color: "#4a4a6a" }}>
                Si un compte admin existe pour <strong>{email}</strong>, un lien de réinitialisation a été envoyé. Il expire dans <strong>30 minutes</strong>.
              </p>
              <p className="text-xs mb-6" style={{ color: "#9ca3af" }}>
                Pensez à vérifier vos spams. Si vous ne recevez rien, contactez le support.
              </p>
              <button onClick={() => { setSubmitted(false); setEmail(""); }}
                className="px-5 py-2.5 rounded-full text-sm font-semibold text-white transition hover:-translate-y-0.5"
                style={{ background: GRAD }} data-testid="forgot-try-again">
                Réessayer
              </button>
            </div>
          ) : (
            <>
              <h1 className="text-3xl font-bold tracking-tight mb-2"
                style={{ fontFamily: "'Space Grotesk', sans-serif", color: "#0d0d1a", letterSpacing: "-0.02em" }}>
                Mot de passe oublié
              </h1>
              <p className="text-sm mb-8" style={{ color: "#4a4a6a" }}>
                Entrez votre email. Si un compte admin existe, un lien de réinitialisation vous sera envoyé.
              </p>
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider block mb-1.5" style={{ color: "#4a4a6a" }}>Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#4a4a6a" }} />
                    <input
                      type="email"
                      value={email}
                      required
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="vous@example.com"
                      className="w-full pl-9 pr-3 py-2.5 rounded-xl border-2 outline-none focus:border-blue-500 transition"
                      style={{ borderColor: "#e2e8f8", color: "#0d0d1a", background: "#fff" }}
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={busy}
                  className="w-full py-3 rounded-full text-white font-bold text-base transition disabled:opacity-50 hover:-translate-y-0.5"
                  style={{ background: GRAD, boxShadow: "0 8px 20px -8px rgba(59,130,246,.5)" }}
                  data-testid="forgot-submit-btn"
                >
                  {busy ? "Envoi en cours…" : "Envoyer le lien"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
